// AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { identifyUser, resetPosthog } from '../utils/posthogClient';
import { getReturnTo, isSafeReturnPath, saveReturnToFromWindow } from '../utils/returnTo';
import {
  HV_JWT_EXPIRED_EVENT,
  HV_JWT_KEY,
  discardInvalidHvJwt,
  getValidHvJwt,
  isHvJwtRequiredPath,
  setAuthorizationHeader,
} from '../utils/hvJwt';
import { getStoredAvatarUrl, isUsablePhotoUrl, storeAvatarUrl } from '../utils/profilePhoto';

/**
 * @typedef {Object} AuthContextValue
 * @property {Record<string, any> | null} user
 * @property {(token: string) => void} acceptToken
 * @property {(email: string, password: string) => Promise<any>} login
 * @property {() => Promise<void>} logout
 * @property {() => void} oauthLogin
 * @property {(values: any) => Promise<any>} signup
 * @property {() => Promise<any>} refetchUser
 * @property {(fields: Record<string, any>) => void} updateProfile
 * @property {boolean} isAdmin
 * @property {boolean} isCandidate
 * @property {boolean} isClient
 */

axios.defaults.withCredentials = true;

const TOKEN_KEY = HV_JWT_KEY;

axios.interceptors.request.use((cfg) => {
  const token = getValidHvJwt();
  if (!token) {
    discardInvalidHvJwt();
    if (isHvJwtRequiredPath(cfg.url, cfg.baseURL)) {
      return Promise.reject(new axios.CanceledError('Skipped: no valid hv_jwt'));
    }
    return cfg;
  }
  cfg.headers = setAuthorizationHeader(cfg.headers, token);
  return cfg;
});

const AuthContext = createContext(
  /** @type {AuthContextValue} */ ({
    user: null,
    acceptToken: (token) => {},
    login: async (email, password) => null,
    logout: async () => {},
    oauthLogin: () => {},
    signup: async (values) => null,
    refetchUser: async () => null,
    updateProfile: () => {},
    isAdmin: false,
    isCandidate: false,
    isClient: false,
  })
);
const API_BASE_URL = '/api';

/** Survives StrictMode remount; resets on full page load or logout. */
let meInFlight = null;
let meHydratedThisPage = false;

/** Routes that use sendmail / scheduleEmail Gmail auth (401 = re-login). */
const SEND_MAIL_LOGOUT_PATHS = ['/api/emails/send', '/api/email/sendusingtemlpate'];

/** @param {string} [url] */
function isSendMailUnauthorizedUrl(url) {
  if (!url) return false;
  const path = url.split('?')[0];
  return SEND_MAIL_LOGOUT_PATHS.some((p) => path === p || path.endsWith(p));
}

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const token = getValidHvJwt();
    if (!token) {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(TOKEN_KEY)) {
        saveReturnToFromWindow();
        localStorage.removeItem(TOKEN_KEY);
      }
      return { user: null, token: null };
    }
    try {
      return { token, user: jwtDecode(token) };
    } catch {
      saveReturnToFromWindow();
      localStorage.removeItem(TOKEN_KEY);
      return { user: null, token: null };
    }
  });

  useEffect(() => {
    const onExpired = () => {
      saveReturnToFromWindow();
      meHydratedThisPage = false;
      meInFlight = null;
      localStorage.removeItem(TOKEN_KEY);
      setAuth({ user: null, token: null });
    };
    window.addEventListener(HV_JWT_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(HV_JWT_EXPIRED_EVENT, onExpired);
  }, []);

  const isAdmin = auth.user && auth.user.role === 'admin';
  const isCandidate = auth.user && auth.user.role === 'candidate';
  const isClient = auth.user && auth.user.role === 'client';


  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/login', { email, password });
      if (response.data && response.data.user) {
        if (response.data.token) {
          localStorage.setItem(TOKEN_KEY, response.data.token);
        }
        setAuth({ user: response.data.user, token: response.data.token });

        identifyUser(response.data.user);
        return response.data.user;

      } else {
        throw new Error('User data is incomplete.');
      }
    } catch (error) {
      console.error('Login failed:', error.response?.data?.message || error.message);
      throw error;
    }
  };

  const oauthLogin = () => {
    const currentPath = window.location.pathname + window.location.search;
    const next = isSafeReturnPath(currentPath) ? currentPath : getReturnTo('/');
    window.location.href = `${API_BASE_URL}/auth/google?next=${encodeURIComponent(next)}`;
  };
  /*  
    const oauthLogin = async (idToken, accessToken) => {
      try {     
        //const response = await axios.post('/api/oauth-login', { token: idToken, accessToken });
        window.location.href = '/api/auth/google';
        if (response.data && response.data.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user)); // Store the user
          return response.data.user;
        } else {
          throw new Error('OAuth login failed');
        }
      } catch (error) {
        console.error('OAuth login failed:', error.response?.data?.message || error.message);
        throw error;
      }
    };
    */
  const signup = async (values) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/signup`, values);
      if (response.data?.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
      }
      setAuth({ user: response.data.user, token: response.data.token });
      identifyUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error('Signup failed:', error.response?.data?.message || error.message);
      throw new Error(error.response?.data?.message || 'Signup failed');
    }
  };
  /*
    const logout = async () => {
      try {
        await axios.get('/api/auth/logout');
        setUser(null);
        setUser({ role: 'guest' });
      } catch (error) {
        console.error('Logout failed:', error);
      }
    };
    /*  helper for the callback page */
  const isLoggingOutRef = useRef(false);

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    saveReturnToFromWindow();

    meHydratedThisPage = false;
    meInFlight = null;

    await axios.get(`${API_BASE_URL}/auth/logout`).catch(() => { });
    localStorage.removeItem(TOKEN_KEY);
    setAuth({ user: null, token: null });
    resetPosthog();
    window.location.href = '/login'; // redirect to login
  }, []);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        const hasToken = !!(auth?.token || localStorage.getItem(TOKEN_KEY));

        if (
          status === 401 &&
          hasToken &&
          isSendMailUnauthorizedUrl(url) &&
          !isLoggingOutRef.current
        ) {
          await logoutRef.current();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [auth?.token]);

  /* helper the callback page will call */
  const acceptToken = (token) => {
    if (typeof token !== 'string' || !token.includes('.')) return;
    resetPosthog();
    localStorage.setItem(TOKEN_KEY, token);
    setAuth({ token, user: jwtDecode(token) });

    /* ✅  tell the extension */
    if (window.chrome?.runtime?.id) {
      //window.chrome.runtime.sendMessage({ hvAction: 'hvSetToken', token });
      window.postMessage(
        { hvForwardToken: true, token },          // payload
        '*'                                       // same‑origin
      );
    }
  };//*/

  const refetchUser = useCallback(async () => {
    const token = getValidHvJwt();
    if (!token) {
      if (auth?.token || (typeof localStorage !== 'undefined' && localStorage.getItem(TOKEN_KEY))) {
        meHydratedThisPage = false;
        saveReturnToFromWindow();
        discardInvalidHvJwt();
        localStorage.removeItem(TOKEN_KEY);
        setAuth({ user: null, token: null });
      }
      return;
    }

    if (meInFlight) return meInFlight;

    meInFlight = (async () => {
      try {
        const response = await axios.get(`/api/me`);
        const updatedUser = response.data;
        const nextToken = updatedUser.token || token;
        const storedPhoto = getStoredAvatarUrl();
        const nextPhoto =
          storedPhoto ||
          (isUsablePhotoUrl(updatedUser.profileImage) ? updatedUser.profileImage : '') ||
          (isUsablePhotoUrl(auth?.user?.profileImage) ? auth.user.profileImage : '');
        if (nextPhoto) storeAvatarUrl(nextPhoto);

        setAuth((prev) => ({
          ...prev,
          user: {
            ...updatedUser,
            profileImage: nextPhoto || updatedUser.profileImage,
          },
          token: nextToken
        }));
        if (updatedUser.token) {
          localStorage.setItem(TOKEN_KEY, updatedUser.token);
        }
        return updatedUser;
      } catch (error) {
        if (error.response?.status === 401) {
          meHydratedThisPage = false;
          saveReturnToFromWindow();
          localStorage.removeItem(TOKEN_KEY);
          setAuth({ user: null, token: null });
        }
        console.error('Failed to refetch user:', error);
      } finally {
        meInFlight = null;
      }
    })();

    return meInFlight;
  }, [auth?.token]);

  const updateProfile = useCallback((fields) => {
    if (!fields || typeof fields !== 'object') return;
    if (isUsablePhotoUrl(fields.profileImage)) {
      storeAvatarUrl(fields.profileImage);
    }
    setAuth((prev) => {
      if (!prev.user) return prev;
      return { ...prev, user: { ...prev.user, ...fields } };
    });
  }, []);

  // Hydrate once per page load. Do not refetch when /api/me rotates the JWT.
  useEffect(() => {
    if (!auth?.token || !auth.user) {
      if (!auth?.token) meHydratedThisPage = false;
      return;
    }
    if (meHydratedThisPage) return;
    meHydratedThisPage = true;
    refetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);


  return (
    <AuthContext.Provider value={{ user: auth.user, acceptToken, login, logout, oauthLogin, signup, refetchUser, updateProfile, isAdmin, isCandidate, isClient }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
